import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Output,
	ViewEncapsulation,
} from '@angular/core';
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { UntypedFormGroup } from '@angular/forms';

import { RestService, IS_NOT_NULL_OR_UNDEFINED } from 'cargo-chief-commons';

import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { UtilService } from '@service/util/util.service';
import { UrlService } from '@service/url/url.service';
import { BaConfigService } from '@service/baConfig/ba-config.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { AccessCacheService } from '@service/access-cache/access-cache.service';

import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';
import { USER_ROLES } from '@domain/constants/app-variables';

import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';

@Component({
	selector: 'app-load-details-form',
	templateUrl: './load-details-form.component.html',
	styleUrls: ['./load-details-form.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadDetailsFormComponent implements OnInit {
	@Input() searchTruckForm;
	@Input() truckSearchIds;
	@Input() emailByTruckIds;
	@Input() truckLaneSearchId;
	@Input() buttonText: string = 'Booking Assistant';
	@Input() lanePricing = null;
	@Input() customerCarrierMatched: boolean;
	@Input() tooltipText: string = 'Email your carriers for rates';
	@Input() inNetworkCarrierCountForSearch: number = null;
	@Input() matchedCarriers: Array<string> = [];
	@Input() matchedCarrierIds: Array<string> = [];
	@Input() disabled: boolean = false;
	@Input() greenScreenRate = null;

	@Input() useButton: boolean = true;
	@Input() useMenuButton: boolean = false;
	@Input() isCityMissing: boolean = false;

	@Output() isSearchRefined: EventEmitter<boolean> = new EventEmitter();

	searchRefined: boolean = true;

	constructor(
		public dialog: MatDialog,
		private cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService,
		public utilService: UtilService,
		public cdRef: ChangeDetectorRef,
		private accessCache: AccessCacheService,
		private baConfigService: BaConfigService,
		private restService: RestService,
		private readonly urlService: UrlService
	) {}

	ngOnInit() {}

	async showNoCarriersRunLaneMsg() {
		let accountCarrierMapCount: any = this.cargochief.getItem(
				CARGOCHIEF_KEYS.ACCOUNT_CARRIER_MAP_COUNT
			),
			modalBody = '',
			modalTitle = '',
			isOutOfNetworkAllowed = this.baConfigService.IS_OUT_OF_NETWORK_ALLOWED,
			isProjectMatch = false;

		accountCarrierMapCount = !this.utilService.isNullOrUndefined(accountCarrierMapCount)
			? Number(JSON.parse(accountCarrierMapCount))
			: 0;
		this.searchRefined = true;

		if (!this.isCityMissing && !this.customerCarrierMatched && !isOutOfNetworkAllowed) {
			const matchedProject = await this.getProject();
			isProjectMatch = IS_NOT_NULL_OR_UNDEFINED(matchedProject);
		}

		if (
			(this.customerCarrierMatched || isOutOfNetworkAllowed || isProjectMatch) &&
			!this.isCityMissing
		) {
			this.cdRef.detach();
			this.openLoadDetailsForm();
			return;
		} else if (accountCarrierMapCount > 0 && !this.customerCarrierMatched && !this.isCityMissing) {
			modalBody = `None of the matching carriers are in your network, and Out of Network carriers is disabled for your account.
			<br/><br/>
			You can enable Out of Network carriers on the <a href="/broker/booking-assistant-automation">Booking Assistant Configuration</a> page, or contact your account administrator.`;
		} else if (accountCarrierMapCount == 0 && !this.isCityMissing) {
			modalTitle = `Booking Assistant Setup`;
			modalBody = `Booking Assistant only emails carriers in your network. Submit your carriers to customercare@cargochief.com to get started.`;
		} else if (this.isCityMissing) {
			modalTitle = `Booking Assistant Setup`;
			modalBody = `Booking Assistant only works for city to city searches. Please refine your search and click Booking Assistant again.`;
			this.searchRefined = false;
		}
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '550px',
			data: {
				title: modalTitle,
				body: modalBody,
				cancelButtonText: '',
				ConfirmButtonText: 'Okay',
				showCloseButton: false,
				buttonActionLayout: 'end center',
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			this.isSearchRefined.emit(this.searchRefined);
		});
	}

	openLoadDetailsForm(): void {
		this.segmentWrapper.fireSegmentEvent(
			SegmentEventsConstants.clicked_book_now,
			this.searchTruckForm
		);
		const dialogRef = this.dialog.open(LoadDetailsFormDialog, {
			width: '950px',
			data: {
				searchTruckForm: this.searchTruckForm,
				truckSearchIds: this.truckSearchIds,
				emailByTruckIds: this.emailByTruckIds,
				truckLaneSearchId: this.truckLaneSearchId,
				lanePricing: this.lanePricing,
				inNetworkCarrierCountForSearch: this.inNetworkCarrierCountForSearch,
				matchedCarrierIds: this.matchedCarrierIds,
				matchedCarriers: this.matchedCarriers,
				greenScreenRate: this.greenScreenRate,
			},
			disableClose: true,
		});

		dialogRef.afterClosed().subscribe((result) => {
			this.cdRef.reattach();
		});
	}

	async getProject() {
		let result = null;
		try {
			const searchRequest = {
				accountId: this.cargochief.ACCOUNT_ID,
				page: 0,
				pageSize: 1,
				originCity: this.searchTruckForm.origin.city,
				originState: this.searchTruckForm.origin.state,
				destinationCity: this.searchTruckForm.destination.city,
				destinationState: this.searchTruckForm.destination.state,
				equipmentTypes: this.searchTruckForm.trailerType.split(','),
			};
			const projectsResponse = await this.restService
				.POST(this.urlService.getProjectsList(), searchRequest)
				.toPromise();
			result = projectsResponse.data[0];
		} catch (error) {}
		return result;
	}
}

@Component({
	selector: 'app-load-details-form-dialog',
	templateUrl: 'modal.template.html',
})
export class LoadDetailsFormDialog implements OnInit {
	isCarrierFilterOpen: boolean = false;
	excludedCarrierIds: Array<any> = [];
	selection: SelectionModel<any> = new SelectionModel<any>(true, []);
	loadDetails: UntypedFormGroup;

	constructor(
		public dialogRef: MatDialogRef<LoadDetailsFormDialog>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {}

	ngOnInit() {}

	closeDialog(): void {
		this.dialogRef.close();
	}

	toggleDialogView() {
		this.isCarrierFilterOpen = !this.isCarrierFilterOpen;
	}

	switchToLoadDetails(selection: any) {
		if (selection) {
			this.selection = selection;
			this.excludedCarrierIds = selection.selected?.map((carrier) => carrier.carrierId);
		}
		this.toggleDialogView();
	}

	switchToCarrierFilter(loadDetails: any) {
		this.loadDetails = loadDetails;
		this.toggleDialogView();
	}
}
